import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ChevronDownIcon, Cross1Icon } from '@radix-ui/react-icons';
import { Avatar, Dialog, ElevatedButton } from '@producer/ui-kit';

import { MemberSelect } from '../components/member-select';
import { GroupAdd } from '../../../components/icons';
import { Note } from '../../../components/note/note';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { RoleDropdown } from '../components/role-dropdown';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { catchError } from '../../../core/catch-error';

import type { RoleTypes } from '../components/role-dropdown';
import type { Member, Project } from '../../../app/entities';
import { createMember } from '../../members/store/member.slice';
import { context } from '../../../app/context';

import styles from './project-collaborator.modal.module.scss';

interface ProjectCollaboratorModalProps {
  project: Project;
  onCancel: () => void;
  isOpenProps?: boolean;
  onClose: () => void;
}

export const ProjectCollaboratorModal: React.FC<ProjectCollaboratorModalProps> = observer(
  ({ project, onCancel, onClose }) => {
    const currentMember = context.member;

    const [actionDisabled, setActionDisabled] = useState<boolean>(false);
    const [hint, setHint] = useState<string>();
    const [serverError, setServerError] = useState<string>();

    const dropdownWidth = React.useRef<number>(0);

    const handleRoleChange = (role?: RoleTypes) => {
      if (role === 'creator') {
        const limit = project.space.seats?.limit || 1;
        const current = project.space.seats?.current || 1;

        if (current < limit) {
          return;
        }

        if (project.space.subscription?.subscriptionId && !project.space.isSelfServing) {
          setHint('All seats taken. Reach out to customer support to extend your plan.');
          setActionDisabled(true);
        } else if (project.space.isSelfServing) {
          setHint("You'll be charged on adding a billable creator.");
        }
      } else {
        setHint(undefined);
        setActionDisabled(false);
      }
    };

    const promoteCollaborator = async (collaborator: Member, role: RoleTypes = 'guest') => {
      if (collaborator.role === role || role === 'guest') {
        return;
      }

      if (role === 'creator') {
        try {
          await collaborator.promote();
          toastPool.insert(
            new Toast(
              `${collaborator.fullName} successfully added as an additional creator. Billing information sent via email.`,
              'success',
            ),
          );
        } catch (error) {
          catchError(error);
        }
      }
    };

    const listContainerCallbackRef = (node: HTMLDivElement) => {
      if (!node) {
        return;
      }
      dropdownWidth.current = node.clientWidth - 72;
    };

    const handleSubmit = async (email: string, role?: RoleTypes) => {
      try {
        let member = project.space.members.find((el) => el.email === email);

        if (!member && role) {
          const members = await createMember({
            spaceId: project.spaceId,
            members: [
              {
                email,
                role,
              },
            ],
          });

          member = members[0];
        }

        if (!member || !member._id) {
          return;
        }

        await project.addCollaborator(member._id);

        onClose();

        toastPool.insert(new Toast(`Successfully invited ${email} to the project`, 'success'));
        setServerError(undefined);
      } catch (error) {
        setServerError('Something went wrong. Please try again');
      }
    };

    return (
      <Dialog title="Manage people in project" onClose={onCancel} mobilePosition="bottom" hideClose>
        <div className={styles['content']}>
          {hint && <Note isFullWidth>{hint}</Note>}

          {serverError ? (
            <div className={styles['server-error']}>
              <Note variant="danger" isFullWidth>
                {serverError}
              </Note>

              {setServerError && (
                <ElevatedButton text="Try again" onClick={() => setServerError(undefined)} />
              )}
            </div>
          ) : (
            <MemberSelect
              space={project.space}
              collaborators={project.collaborators}
              actionDisabled={actionDisabled}
              onSubmit={handleSubmit}
              onRoleChange={handleRoleChange}
            />
          )}
        </div>

        {project.collaborators?.length ? (
          <div className={styles['list']} ref={listContainerCallbackRef}>
            {project.collaborators.map((collaborator) => {
              const { email, _id, fullName } = collaborator;

              return (
                <div className={styles['collaborator-item']} key={_id}>
                  <div className={styles['left']}>
                    <Avatar src={collaborator.avatar} initials={collaborator.initials} />

                    <div className={styles['member-info']}>
                      <span>{fullName}</span>
                      <span>{email}</span>
                    </div>
                  </div>

                  <div className={styles['right']}>
                    {collaborator.role === 'guest' ? (
                      <RoleDropdown
                        trigger={(label) => (
                          <button className={styles['trigger']}>
                            <Tooltip text="Promote to creator">
                              <div className={styles['tooltip-child']}>
                                <div className={styles['role-label']}>{label}</div>

                                <ChevronDownIcon color="var(--color-grayscale-shuttle)" />
                              </div>
                            </Tooltip>
                          </button>
                        )}
                        space={project.space}
                        role={collaborator.role}
                        width={dropdownWidth.current}
                        onChange={(role) => promoteCollaborator(collaborator, role)}
                      />
                    ) : (
                      <div className={styles['role-label']}>{collaborator.role || 'guest'}</div>
                    )}

                    {['owner', 'admin'].includes(currentMember.role) && (
                      <Tooltip text="Remove from project">
                        <button
                          className={styles['close-button']}
                          onClick={() => project.removeCollaborator(collaborator._id)}
                        >
                          <Cross1Icon />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles['collaborators-placeholder']}>
            <GroupAdd />

            <span>No people in this project yet.</span>
          </div>
        )}
      </Dialog>
    );
  },
);
